import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AchievementBadges from './AchievementBadges';
import CategoryCompletionScreen from './CategoryCompletionScreen';
import { gameApi, userApi, prizeApi } from '../api';
import { Question, GameState, UserStats, AnswerResult } from '../types/game';
import { Prize } from '../types/prizes';
import './GameComponent.css';
import { appInsights } from '../ApplicationInsightsService';

interface CurrentQuestionState {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
}

interface LocationState {
  selectedCategory?: string;
  username?: string;
}

const STREAK_THRESHOLD = 5;
export const LINK_TO_SUBMIT_QUESTIONS = 'https://forms.gle/qDntXH4uVy8an34k6';

export const GameComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCategory, username } = (location.state as LocationState) || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [currentQuestionState, setCurrentQuestionState] = useState<CurrentQuestionState | null>(null);
  const [displayUsername, setDisplayUsername] = useState<string>(username || '');
  const [showExplanation, setShowExplanation] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [aiResponse, setAiResponse] = useState<string>('');
  const [llmPercentage, setLlmPercentage] = useState<number>(0);
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(true);
  const [expandedAnswers, setExpandedAnswers] = useState<number[]>([]);
  const [nextPrize, setNextPrize] = useState<Prize | null>(null);
  const [explanation, setExplanation] = useState<string>('');
  const [correctAnswer, setCorrectAnswer] = useState<number>(0);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<boolean>(false);
  const [currentStreak, setCurrentStreak] = useState<number>(0);
  const [showSubmissionBanner, setShowSubmissionBanner] = useState<boolean>(false);
  const [clickedAnswerIndex, setClickedAnswerIndex] = useState<number | null>(null);
  const [questionTimer, setQuestionTimer] = useState<number>(60);
  const [feedbackGiven, setFeedbackGiven] = useState<boolean>(false);
  const [correctAnswers, setCorrectAnswers] = useState<number>(0);
  const [clickedAnswer, setClickedAnswer] = useState<boolean>(false);
  // Question timer logic
  useEffect(() => {
    if (gameState?.current_question_start_time) {
      const startTime = new Date(gameState.current_question_start_time).getTime();
      const interval = setInterval(() => {
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - startTime) / 1000);
        const remainingSeconds = Math.max(60 - elapsedSeconds, 0);
        setQuestionTimer(remainingSeconds);
        
        if (remainingSeconds === 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval); // Cleanup interval on unmount
    }
  }, [gameState?.current_question_start_time]);
  
  // Calculate points multiplier based on remaining time
  const calculateMultiplier = (): number => {
    if (questionTimer > 30) {
      return 3; // More than half time remaining = 3x multiplier
    } else if (questionTimer > 0) {
      return 2; // Between half and zero time remaining = 2x multiplier
    } else {
      return 1; // No time remaining = 1x multiplier
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Check if banner was previously dismissed
  useEffect(() => {
    const bannerDismissed = localStorage.getItem('submissionBannerDismissed') === 'true';
    setShowSubmissionBanner(!bannerDismissed);
  }, []);

  // Update streak and banner visibility when answer is submitted
  useEffect(() => {
    if (isAnswerCorrect) {
      setCurrentStreak(prev => prev + 1);
    } else {
      setCurrentStreak(0);
    }
  }, [isAnswerCorrect]);

  const handleDismissBanner = useCallback(() => {
    setShowSubmissionBanner(false);
    localStorage.setItem('submissionBannerDismissed', 'true');
  }, []);

  const handleSubmitQuestions = useCallback(() => {
    window.open(LINK_TO_SUBMIT_QUESTIONS, '_blank');
  }, []);

  const handleError = useCallback((error: any, message: string) => {
    console.error(message, error);
    setError(message);
    setLoading(false);
  }, []);

  const updateCurrentQuestion = useCallback((gameState: GameState) => {
    if (gameState.current_question) {
      setCurrentQuestionState({
        question: gameState.current_question,
        questionNumber: gameState.questions_answered + 1,
        totalQuestions: gameState.total_questions
      });
    }
  }, []);

  const initializeGame = useCallback(async (category?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await gameApi.startGame(category);
      
      if (response.data.status === 'success') {
        const gameState = response.data.game_state;
        if (response?.data?.game_state?.total_questions === 0) {
          navigate('/category-completion', { 
            state: { categoryName: selectedCategory || 'current' }
          });
        }
        setGameState(gameState);
        updateCurrentQuestion(gameState);
      } else {
        setError('Failed to start game');
      }
    } catch (error) {
      handleError(error, "Failed to start game");
    } finally {
      setLoading(false);
    }
  }, [handleError, updateCurrentQuestion]);

  // Effect to start game when component mounts
  useEffect(() => {
    const loadGame = async () => {
      try {
        setLoading(true);
        const [_, prizesResponse] = await Promise.all([
          selectedCategory ? initializeGame(selectedCategory) : userApi.getProfile().then(
            r => {
              if (!username) {
                setDisplayUsername(r.data.user?.username || '');
              }
              initializeGame(r.data.user?.current_category);
            }
          ),
          prizeApi.getUserPrizes()
        ]) as [any, any]; // Type assertion to fix the linter error

        if (prizesResponse.data.status === 'success') {
          setNextPrize(prizesResponse.data.next_prize);
        }


      } catch (error) {
        handleError(error, "Failed to initialize game");
      }
    };

    loadGame();
  }, [initializeGame, handleError, selectedCategory, username, navigate]);

  // Add new effect to watch for level-up changes
  useEffect(() => {
    if (gameState?.balance_level) {
      const updatePrizes = async () => {
        try {
          const prizesResponse = await prizeApi.getUserPrizes();
          if (prizesResponse.data.status === 'success') {
            setNextPrize(prizesResponse.data.next_prize);
          }
        } catch (error) {
          console.error('Failed to update prizes after level-up:', error);
        }
      };
      updatePrizes();
    }
  }, [gameState?.balance_level]); // Only run when balance_level changes

  const handleStartNewGame = useCallback(() => {
    initializeGame();
  }, [initializeGame]);

  const resetUIState = useCallback(() => {
    setShowExplanation(false);
    setSelectedAnswer(null);
    setAiResponse('');
    setLlmPercentage(0);
    setIsQuestionExpanded(true);
    setExpandedAnswers([]);
    setClickedAnswerIndex(null);
    setFeedbackGiven(false);
    setClickedAnswer(false);
  }, []);

  // Prevent copy paste
  const preventCopyPaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    return false;
  }, []);

  // Prevent text selection
  const preventSelection = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  }, []);

  const handleAnswer = async (answerIndex: number) => {
    try {
      setClickedAnswerIndex(answerIndex);
      setClickedAnswer(true);
      // Only fetch answer response, prizes will be updated by the effect above
      const answerResponse = await gameApi.submitAnswer(answerIndex);

      if (answerResponse.data.status === 'success') {
        const { game_state, answer_result } = answerResponse.data;
        setGameState(game_state);
        setSelectedAnswer(answerIndex);
        setShowExplanation(true);
        
        const result = answer_result as AnswerResult;
        setExplanation(result.explanation);
        setCorrectAnswer(result.correct_answer);
        setIsAnswerCorrect(result.is_correct);
        setAiResponse(result.ai_response);
        setLlmPercentage(Number((result.llm_percentage * 100).toFixed(1)));
        if (result.is_correct) {
          setCorrectAnswers(prev => prev + 1);
        }
      }
    } catch (error) {
      handleError(error, "Failed to submit answer");
    }
  };

  const handleNextQuestion = async () => {
    try {
      window.scrollTo(0, 0);
      resetUIState();
      setClickedAnswerIndex(null);
      const response = await gameApi.getNextQuestion();
      
      if (response.data.game_state.is_completed) {
        navigate('/results', { 
          state: { 
            finalScore: response.data.game_state.score,
            correctAnswers: correctAnswers,
            totalQuestions: response.data.game_state.total_questions,
            newlyUnlockedAchievements: response.data.game_state.newly_unlocked_achievements,
            llmAvgPercentage: response.data.game_state.llm_avg_percentage*100,
            category: response.data.game_state.category
          }
        });
        return;
      }
      
      const newGameState = response.data.game_state;
      setGameState(newGameState);
      updateCurrentQuestion(newGameState);
    } catch (err) {
      handleError(err, 'Failed to get next question');
    }
  };

  const sendFeedback = (questionId: number, isThumbsUp: boolean) => {
    const feedbackType = isThumbsUp ? 'ThumbsUp' : 'ThumbsDown';
    appInsights.trackEvent({
      name: 'Feedback',
      properties: {
        questionId,
        feedbackType,
      },
    });
    setFeedbackGiven(true);
  };

  if (loading) return <div className="App">Loading...</div>;
  if (error) {
    return (
      <div className="App">
        <div className="error-message">{error}</div>
        <button onClick={handleStartNewGame} className="next-button">
          Start New Game
        </button>
      </div>
    );
  }
  if (!gameState || !currentQuestionState) {
    return <div className="App">No question available.</div>;
  }

  return (
    <div className="App">
      {showSubmissionBanner && currentStreak >= STREAK_THRESHOLD && (
        <div className="submission-banner">
          <div className="banner-content">
            <div className="banner-icon">💡</div>
            <div className="banner-text">Have security expertise? Submit your own questions!</div>
            <div className="banner-actions">
              <button className="submit-button" onClick={handleSubmitQuestions}>Submit</button>
              <button className="close-button" onClick={handleDismissBanner}>×</button>
            </div>
          </div>
        </div>
      )}
      
      <div className="game-header">
        <div className="game-title">
          <div className="title">AI Showdown:</div>
          <div className="subtitle">Security Expertise</div>
        </div>
        
        <div className="player-stats">
          <div className="stat-item">
            <div className="label">challenger</div>
            <div className="player-name">{displayUsername}</div>
          </div>
          <div className="stat-item">
            <div className="label">score</div>
            <div className="score">{gameState.score}</div>
          </div>
        </div>
      </div>

      {/* <div className="challenge-progress">
        <div className="progress-text">
          <span>
            {gameState.balance_level}/{nextPrize?.required_level_ups}
          </span>
          {' '}level-ups to win a {nextPrize?.name}!
        </div>
        <div className="balance-bar-container">
          <div className="balance-bar" data-balance={gameState.balance}>
            <div className="balance-line" />
            <div 
              className="balance-fill positive"
              style={{ 
                transform: `scaleX(${gameState.balance > 0 ? gameState.balance/4 : 0})` 
              }}
            />
            <div 
              className="balance-fill negative"
              style={{ 
                transform: `scaleX(${gameState.balance < 0 ? Math.abs(gameState.balance)/4 : 0})` 
              }}
            />
          </div>
          <div className="balance-labels">
            <span className="wrong">Prev. Level</span>
            <span className="right">Next Level</span>
          </div>
        </div>
      </div> */}

      <div className="question-container">
        <div className="timer">
          <div className="timer-display">
            Time Remaining: {formatTime(questionTimer)}
            <span className="multiplier">Points ×{calculateMultiplier()}</span>
          </div>
        </div>
        <div className="collapsible-section question-section">
          <div 
            className="expandable-text"
            onClick={() => setIsQuestionExpanded(!isQuestionExpanded)}
          >
            <div className="question-label">
              <span className="question-number">
                Question {currentQuestionState.questionNumber}/{currentQuestionState.totalQuestions}
              </span>
            </div>
            <div 
              className={`nonSelectable text-content ${isQuestionExpanded ? 'expanded' : ''}`}
              onCopy={preventCopyPaste}
              onCut={preventCopyPaste}
              onPaste={preventCopyPaste}
              onMouseDown={preventSelection}
            >
              {currentQuestionState.question.question}              
              <span className="expand-arrow">{isQuestionExpanded ? '▼' : '◀'}</span>
            </div>
          </div>
        </div>

        <div className="answers">
          {currentQuestionState.question.answers.map((answer: string, index: number) => (
            <div key={index} className="answer-section">
              <div 
                className={`expandable-text answer-header ${
                  showExplanation
                    ? index === selectedAnswer
                      ? isAnswerCorrect
                        ? 'correct'
                        : 'wrong'
                      : index === correctAnswer
                        ? 'correct'
                        : ''
                    : ''
                }`}
                onClick={() => {
                  setExpandedAnswers(prev => 
                    prev.includes(index) 
                      ? prev.filter(i => i !== index)
                      : [...prev, index]
                  );
                }}
              >
                <div className="answer-label">Answer {String.fromCharCode(65 + index)}</div>
                <div 
                  className={`nonSelectable text-content ${expandedAnswers.includes(index) ? 'expanded' : ''}`}
                  onCopy={preventCopyPaste}
                  onCut={preventCopyPaste}
                  onPaste={preventCopyPaste}
                  onMouseDown={preventSelection}
                >
                  {answer}
                  <span className="expand-arrow">
                    {expandedAnswers.includes(index) ? '▼' : '◀'}
                  </span>
                </div>
              </div>
              {expandedAnswers.includes(index) && !showExplanation && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAnswer(index);
                  }}
                  className={`select-answer-button ${clickedAnswer ? 'disabled' : ''}`}
                  disabled={clickedAnswer}
                >
                  {clickedAnswer ? 'Processing...' : 'Select this answer'}
                </button>
              )}
            </div>
          ))}
        </div>

        {showExplanation && (
          <div className="explanation">
            <div className="llm-comparison">
              {selectedAnswer === correctAnswer ? (
                <div className="success-message">
                  <div className="llm-percentage">
                    {Number(llmPercentage).toFixed(1)}% of small LLMs got it correct!
                  </div>
                </div>
              ) : (
                <div className="failure-message">
                  <div className="llm-percentage">
                    {Number(llmPercentage).toFixed(1)}% of small LLMs beat you!
                  </div>
                  <div className="ai-quote">
                    {aiResponse}
                  </div>
                </div>
              )}
            </div>
            <h3>Explanation:</h3>
            <p>{explanation}</p>

            <button onClick={handleNextQuestion} className="next-button">
              {currentQuestionState?.questionNumber === currentQuestionState?.totalQuestions ? 'End Game' : 'Next Question'}
            </button>
          </div>
        )}
        <div className="feedback-section">
          <div className="feedback-text">Question Quality:</div>
          <div className="feedback-buttons">
            <button 
              onClick={() => sendFeedback(currentQuestionState.question.id, true)} 
              className="feedback-button thumbs-up"
              aria-label="Good question"
              disabled={feedbackGiven}
            >
              <span className="feedback-icon thumbs-up-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                </svg>
              </span>
            </button>
            <button 
              onClick={() => sendFeedback(currentQuestionState.question.id, false)} 
              className="feedback-button thumbs-down"
              aria-label="Poor question"
              disabled={feedbackGiven}
            >
              <span className="feedback-icon thumbs-down-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.58-6.59c.37-.36.59-.86.59-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="achievements-section-main">
        <div className="section-title">Achievement Badges</div>
        <AchievementBadges 
          unlockedAchievements={gameState.achievements || []}
        />
      </div>
    </div>
  );
};

export default GameComponent; 